import {
	Component,
	OnInit,
	inject,
	ElementRef,
	viewChild,
	effect
} from '@angular/core';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GenericState } from 'src/app/store/generic.state';
import { SignupQuestionsSignal } from 'src/app/signal/signup-questions.signal';
import { Store } from '@ngxs/store';
import * as _ from 'underscore';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NgClass } from '@angular/common';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { InterestedRoles } from './interested-roles/interested-roles';
import { LookupWithSubModel } from 'src/app/models/lookupModel';
import { filter, take } from 'rxjs';

@Component({
	selector: 'app-first-step',
	templateUrl: './first-step.component.html',
	styleUrls: ['./first-step.component.scss'],
	imports: [FormsModule, ReactiveFormsModule, NzGridModule, NgClass, NzSpaceModule, NzSelectModule, InterestedRoles]
})
export class FirstStepComponent implements OnInit {
	readonly inputElement = viewChild<ElementRef>('inputElement');
	public form: FormGroup;
	
	private readonly signupQuestionsSignal = inject(SignupQuestionsSignal);
	private readonly store = inject(Store);
	private readonly genericActions$ = this.store.select(GenericState.getGeneric);

	public isFormEnabled = false;

	public jobProfiles: any[] = [];
	public jobCategories: LookupWithSubModel[] = [];
	public specializations: any[] = [];
	public experience = [
		{ label: '0-1 year', value: 0 },
		{ label: '1-2 years', value: 1 },
		{ label: '2-3 years', value: 2 },
		{ label: '3-4 years', value: 3 },
		{ label: '5-10 years', value: 5 },
		{ label: '11 and up', value: 11 }
	];
	private isPatching = false;

	constructor() {
		// Effect to watch job profiles from signal
		effect(() => {
			const profiles = this.signupQuestionsSignal.jobProfiles();
			this.jobProfiles = profiles;
			
			if (profiles.length === 0) {
				this.isFormEnabled = true;
			} else {
				this.isFormEnabled = false;
			}
		});

		// Effect to watch roles experience for form patching
		effect(() => {
			const rolesExp = this.signupQuestionsSignal.rolesExperience();
			if (!_.isEmpty(rolesExp) && this.jobCategories.length > 0 && !this.isPatching) {
				this.isPatching = true;
				this.form?.patchValue({
					jobCategoryId: Number.parseInt(rolesExp.jobCategoryId),
					yearsOfExperience: rolesExp.yearsOfExperience,
					specializations: rolesExp.specializations?.map((e: any) => e.id)
				});
				this.form?.updateValueAndValidity();
				this.isPatching = false;
			}
		});
	}

	public ngOnInit(): void {
		this.buildForm();
		this.setupFormValueChanges();
		this.loadJobCategories();
	}

	private setupFormValueChanges(): void {
		// Watch job category changes to update specializations
		this.form.get('jobCategoryId')?.valueChanges.subscribe((value) => {
			this.specializations =
				this.jobCategories.find((e) => e.id === value)?.subOptions || [];
			this.form.patchValue({ specializations: null });
		});

		// Watch all form value changes to update state
		this.form.valueChanges.subscribe((value) => {
			if (this.isPatching) return;
			
			const getJobCategory = this.jobCategories.find(
				(e) => e.id === value.jobCategoryId
			);
			const dataSet = {
				specializations: getJobCategory?.subOptions
					.filter((e: any) => value.specializations?.includes(e.id))
					.map((e: any) => ({ id: e.id, option: e.option })),
				jobCategoryLabel: getJobCategory?.option ?? undefined,
				jobCategoryId: value.jobCategoryId
					? value.jobCategoryId.toString()
					: undefined,
				yearsOfExperience: value.yearsOfExperience,
				isFormValid: this.form.valid && this.jobProfiles.length > 0
			};

			this.signupQuestionsSignal.patchStepOne(dataSet);
		});
	}

	private loadJobCategories(): void {
		this.genericActions$
			.pipe(
				filter((data: any) => !_.isEmpty(data.jobCategoriesWithSubOptions)),
				take(1)
			)
			.subscribe((filteredData: any) => {
				this.jobCategories = filteredData.jobCategoriesWithSubOptions;
				
				// Trigger form patching after categories are loaded
				const rolesExp = this.signupQuestionsSignal.rolesExperience();
				if (!_.isEmpty(rolesExp)) {
					this.isPatching = true;
					this.form.patchValue({
						jobCategoryId: Number.parseInt(rolesExp.jobCategoryId),
						yearsOfExperience: rolesExp.yearsOfExperience,
						specializations: rolesExp.specializations?.map((e: any) => e.id)
					});
					this.form.updateValueAndValidity();
					this.isPatching = false;
				}
			});
	}

	private buildForm(): void {
		this.form = new FormGroup({
			jobCategoryId: new FormControl(null, Validators.required),
			specializations: new FormControl(null, Validators.required),
			yearsOfExperience: new FormControl(null, Validators.required)
		});
	}

	public deleteItem(e: any): void {
		this.signupQuestionsSignal.removeJobProfile(e.roleId);
	}
}
